import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { formatDateTime } from '../../utils/dateFormat'
import { formatOrderAmount } from '../../utils/ordersListUtils'
import {
  copyTextToClipboard,
  formatDash,
  formatTransferType,
  getAdminPaymentProofDetailFields,
  getPaymentProcessingPreviewStatusLabel,
  getPaymentProofItemStatusLabel,
  isPaymentProofFileImage,
  isPaymentProofFilePdf,
  mapPaymentProcessingRowToPreviewProofs,
} from '../../utils/paymentProofDisplay'
import PaymentProofFileViewer from './PaymentProofFileViewer'
import PaymentProofVerifyDialog from './PaymentProofVerifyDialog'

const PAYMENT_PROOF_REJECT_REASON_MAX = 500

function PreviewDetailRow({ label, value, copyable = false }) {
  const display = formatDash(value)
  const [copied, setCopied] = useState(false)
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        py: 1.25,
        borderBottom: '1px solid #eee',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right', minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
          {display}
        </Typography>
        {copyable && display !== '—' ? (
          <Button
            size="small"
            sx={{ mt: 0.25, minWidth: 0, p: 0, textTransform: 'none' }}
            onClick={async () => {
              const ok = await copyTextToClipboard(display)
              if (ok) {
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
              }
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        ) : null}
      </Box>
    </Box>
  )
}

function formatAmountPaid(value) {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n)) return '—'
  return `৳${n.toFixed(2)}`
}

function proofStatusChipColor(status) {
  const upper = String(status || '').toUpperCase()
  if (upper === 'VERIFIED') return 'success'
  if (upper === 'REJECTED') return 'error'
  return 'warning'
}

export default function AdminPaymentProofPreviewDialog({
  open,
  row,
  onClose,
  canVerify = false,
  onUpdated,
  onVerifyProof,
  onRejectProof,
  proofActionSubmitting = false,
}) {
  const proofs = useMemo(
    () => (row ? mapPaymentProcessingRowToPreviewProofs(row) : []),
    [row],
  )
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [verifyTarget, setVerifyTarget] = useState(null)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectError, setRejectError] = useState(null)
  const [actionError, setActionError] = useState(null)

  useEffect(() => {
    if (!open) return
    const idx = proofs.findIndex(
      (p) => Number(p.id) === Number(row?.proof_id),
    )
    setSelectedIndex(idx >= 0 ? idx : 0)
    setVerifyTarget(null)
    setRejectOpen(false)
    setRejectReason('')
    setRejectError(null)
    setActionError(null)
    // Reset when the deposit/row identity changes; avoid depending on `proofs`
    // so sibling list refreshes do not wipe the user's selected file.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- proofs derived from row
  }, [open, row?.request_id, row?.proof_id])

  const selectedProof = proofs[selectedIndex] ?? proofs[0] ?? null
  const depositStatusLabel = row ? getPaymentProcessingPreviewStatusLabel(row) : '—'
  const depositStatusUpper = String(row?.request_status || row?.status || '').toUpperCase()
  const depositRejectionReason = String(row?.rejection_reason || '').trim()

  const pendingProofCount = proofs.filter(
    (p) => String(p.verification_status || 'PENDING').toUpperCase() === 'PENDING',
  ).length
  const selectedIsPending =
    selectedProof &&
    String(selectedProof.verification_status || 'PENDING').toUpperCase() ===
      'PENDING'
  // Prefer API flag when viewing the list row's proof; otherwise derive from
  // sibling proofs (backend now returns all proofs on the request).
  const isLastPendingForSelected = Boolean(
    selectedIsPending &&
      (Number(selectedProof?.id) === Number(row?.proof_id) &&
      row?.is_last_pending_on_request != null
        ? row.is_last_pending_on_request
        : pendingProofCount === 1),
  )

  const selectedProofStatus = selectedProof?.verification_status
    ? getPaymentProofItemStatusLabel(selectedProof.verification_status)
    : depositStatusLabel

  const detailFields = selectedProof
    ? getAdminPaymentProofDetailFields(selectedProof)
    : []

  const depositPending = depositStatusUpper === 'PENDING'
  const proofPending =
    selectedProof &&
    String(selectedProof.verification_status || 'PENDING').toUpperCase() ===
      'PENDING'
  const showProofActions = canVerify && depositPending && proofPending

  const handleConfirmVerifyProof = async (payload) => {
    if (!selectedProof?.id || !row?.request_id) return
    setActionError(null)
    try {
      await onVerifyProof?.({
        requestId: row.request_id,
        proofId: selectedProof.id,
        payload,
        isLastPending: isLastPendingForSelected,
      })
      setVerifyTarget(null)
      onUpdated?.()
    } catch (err) {
      throw err
    }
  }

  const handleConfirmRejectProof = async () => {
    const reason = rejectReason.trim()
    if (!reason) {
      setRejectError('Rejection reason is required.')
      return
    }
    if (!selectedProof?.id || !row?.request_id) return
    try {
      setRejectError(null)
      await onRejectProof?.({
        requestId: row.request_id,
        proofId: selectedProof.id,
        reason,
      })
      setRejectOpen(false)
      setRejectReason('')
      onUpdated?.()
    } catch (err) {
      setRejectError(err?.message || 'Failed to reject proof')
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            pr: 1,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Payment proof preview
            </Typography>
            {row?.deposit_id ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {row.deposit_id}
                {row.shop_name ? ` · ${row.shop_name}` : ''}
                {row.order_id ? ` · Order ${row.order_id}` : ''}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} aria-label="Close" sx={{ color: '#666' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {actionError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          ) : null}
          {proofs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No payment proof files on this deposit.
            </Typography>
          ) : (
            <Stack spacing={2.5}>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Chip
                  size="small"
                  label={`Deposit: ${depositStatusLabel}`}
                  color={proofStatusChipColor(depositStatusUpper)}
                  variant="outlined"
                />
                {row?.amount != null ? (
                  <Chip
                    size="small"
                    label={`Total ${formatOrderAmount(row.amount)}`}
                    variant="outlined"
                  />
                ) : null}
              </Stack>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Files
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Select a file to view it and its payment details
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                  {proofs.map((proof, index) => {
                    const url = proof.file_url
                    const isSelected = index === selectedIndex
                    const isImage = isPaymentProofFileImage(url)
                    const isPdf = isPaymentProofFilePdf(url)
                    const itemStatus = getPaymentProofItemStatusLabel(
                      proof.verification_status,
                    )
                    return (
                      <Box key={proof.id ?? index} sx={{ position: 'relative' }}>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setSelectedIndex(index)}
                          sx={{
                            width: 112,
                            height: 112,
                            p: 0,
                            border: '2px solid',
                            borderColor: isSelected ? '#11809A' : '#e0e0e0',
                            borderRadius: 1,
                            bgcolor: isSelected ? 'rgba(17, 128, 154, 0.06)' : '#fafafa',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                          }}
                        >
                          {url && isImage ? (
                            <Box
                              component="img"
                              src={url}
                              alt=""
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Box sx={{ px: 1 }}>
                              <DescriptionOutlinedIcon sx={{ color: '#888', mb: 0.5 }} />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ lineHeight: 1.2 }}
                              >
                                {isPdf
                                  ? 'PDF'
                                  : !url &&
                                      String(proof.payment_method || '').toUpperCase() ===
                                        'EXISTING_BALANCE'
                                    ? 'Existing balance'
                                    : proof.file_label}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Chip
                          label={itemStatus}
                          size="small"
                          color={proofStatusChipColor(proof.verification_status)}
                          variant="outlined"
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            left: 4,
                            maxWidth: 104,
                            height: 20,
                            fontSize: 10,
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      </Box>
                    )
                  })}
                </Box>
              </Box>

              {selectedProof ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Document
                  </Typography>
                  {String(selectedProof.payment_method || '').toUpperCase() ===
                    'EXISTING_BALANCE' || !selectedProof.file_url ? (
                    <Typography variant="body2" color="text.secondary">
                      {String(selectedProof.payment_method || '').toUpperCase() ===
                      'EXISTING_BALANCE'
                        ? 'Existing balance'
                        : 'No file to display.'}
                    </Typography>
                  ) : (
                    <PaymentProofFileViewer
                      fileUrl={selectedProof.file_url}
                      title={selectedProof.file_label}
                    />
                  )}
                </Box>
              ) : null}

              {selectedProof ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Payment details
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1 }}
                    noWrap
                  >
                    {selectedProof.file_label}
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 1,
                      px: 2,
                      bgcolor: '#fff',
                    }}
                  >
                    <PreviewDetailRow label="Status" value={selectedProofStatus} />
                    {String(selectedProof.verification_status || '').toUpperCase() ===
                      'REJECTED' &&
                    depositRejectionReason ? (
                      <PreviewDetailRow
                        label="Rejection reason"
                        value={depositRejectionReason}
                      />
                    ) : null}
                    <PreviewDetailRow
                      label="Payment method"
                      value={formatTransferType(selectedProof.payment_method)}
                    />
                    {detailFields.map((field) => (
                      <PreviewDetailRow
                        key={field.key}
                        label={field.label}
                        value={field.value}
                        copyable={Boolean(field.copyable)}
                      />
                    ))}
                    <PreviewDetailRow
                      label="Amount paid"
                      value={formatAmountPaid(selectedProof.requested_amount)}
                    />
                    <PreviewDetailRow
                      label="Payment date & time"
                      value={
                        selectedProof.payment_at
                          ? formatDateTime(selectedProof.payment_at)
                          : null
                      }
                    />
                    <PreviewDetailRow
                      label="Shwapno receiving account"
                      value={selectedProof.shwapno_receiving_account_label}
                    />
                  </Box>
                </Box>
              ) : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {showProofActions ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  disabled={proofActionSubmitting}
                  onClick={() =>
                    setVerifyTarget({
                      id: selectedProof.id,
                      requested_amount: selectedProof.requested_amount,
                    })
                  }
                  sx={{
                    bgcolor: '#11809A',
                    '&:hover': { bgcolor: '#0f6d82' },
                    textTransform: 'none',
                  }}
                >
                  Verify this proof
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={proofActionSubmitting}
                  onClick={() => {
                    setRejectOpen(true)
                    setRejectReason('')
                    setRejectError(null)
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Reject this proof
                </Button>
              </>
            ) : null}
          </Box>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <PaymentProofVerifyDialog
        open={Boolean(verifyTarget)}
        request={verifyTarget}
        title="Verify this payment proof"
        requireSapDocNo={isLastPendingForSelected}
        onClose={() => {
          if (proofActionSubmitting) return
          setVerifyTarget(null)
        }}
        onConfirm={handleConfirmVerifyProof}
        submitting={proofActionSubmitting}
      />

      <Dialog
        open={rejectOpen}
        onClose={() => {
          if (proofActionSubmitting) return
          setRejectOpen(false)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Reject this payment proof</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {rejectError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRejectError(null)}>
              {rejectError}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Reject proof for {row?.deposit_id || 'this deposit'}? The whole deposit will be
            marked rejected if any proof is rejected.
          </Typography>
          <TextField
            autoFocus
            label="Rejection reason"
            value={rejectReason}
            onChange={(e) => {
              const value = e.target.value
              if (value.length <= PAYMENT_PROOF_REJECT_REASON_MAX) {
                setRejectReason(value)
                setRejectError(null)
              }
            }}
            fullWidth
            multiline
            rows={3}
            required
            disabled={proofActionSubmitting}
            helperText={`${rejectReason.length}/${PAYMENT_PROOF_REJECT_REASON_MAX} characters`}
            inputProps={{ maxLength: PAYMENT_PROOF_REJECT_REASON_MAX }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectOpen(false)}
            disabled={proofActionSubmitting}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={proofActionSubmitting || !rejectReason.trim()}
            onClick={handleConfirmRejectProof}
            startIcon={
              proofActionSubmitting ? <CircularProgress size={16} /> : undefined
            }
          >
            Reject proof
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
